import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { Layers, Plus } from "lucide-react";
import { PATHS } from "@/lib/router";
import { customFrameworkStore } from "../stores/custom-framework.store";
import { FrameworkCard } from "../components/FrameworkCard";
import { FrameworkFormDialog } from "../components/FrameworkFormDialog";
import { EmptyState } from "../components/EmptyState";
import { PageHeader, ConfirmDialog } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { showToast } from "@/lib/utils/toast";

const store = customFrameworkStore;

function NewFrameworkButton({ onClick }) {
  return (
    <Button type="button" onClick={onClick}>
      <Plus className="mr-1.5 h-4 w-4" />
      New Framework
    </Button>
  );
}

export const CustomFrameworkListPage = observer(
  function CustomFrameworkListPage() {
    const navigate = useNavigate();
    const [formOpen, setFormOpen] = useState(false);
    const [formTarget, setFormTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);

    useEffect(() => {
      store.fetchFrameworks();
    }, []);

    const openCreate = () => {
      setFormTarget(null);
      setFormOpen(true);
    };

    const openEdit = (framework) => {
      setFormTarget(framework);
      setFormOpen(true);
    };

    // ── Create / Edit ─────────────────────────────────────────────────────────────
    const handleFrameworkSubmit = async (values, logoFile) => {
      const isEdit = Boolean(formTarget);
      const promise = isEdit
        ? store.updateFramework(formTarget._id, values)
        : store.createFramework(values);

      showToast.promise(promise, {
        loading: isEdit ? "Updating framework..." : "Creating framework...",
        success: isEdit
          ? "Framework updated"
          : "Framework created successfully",
        error: (err) =>
          err?.message ||
          (isEdit
            ? "Failed to update framework"
            : "Failed to create framework"),
      });

      const result = await promise;
      const frameworkId = isEdit ? formTarget._id : result._id;

      if (logoFile) {
        try {
          await store.uploadLogo(frameworkId, logoFile);
        } catch (err) {
          showToast.error(err?.message || "Failed to upload logo");
        }
      }

      if (!isEdit) {
        navigate(PATHS.CUSTOM_FRAMEWORK_BUILDER.replace(":id", frameworkId));
      }
    };

    // ── Delete ────────────────────────────────────────────────────────────────────
    const handleDelete = async () => {
      if (!deleteTarget) return;
      const promise = store.deleteFramework(deleteTarget._id);
      showToast.promise(promise, {
        loading: "Deactivating framework...",
        success: "Framework deactivated",
        error: (err) => err?.message || "Failed to deactivate framework",
      });
      await promise;
      setDeleteTarget(null);
    };

    return (
      <div className="flex-1 flex flex-col min-h-0 gap-y-6">
        <PageHeader
          title="Custom Frameworks"
          description="Define your own ESG frameworks and disclosures for your company."
          icon={Layers}
          actions={<NewFrameworkButton onClick={openCreate} />}
        />

        <div>
          {store.loading ? (
            <div className="grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="rounded-lg border p-5 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-3 flex-1">
                      <Skeleton className="h-10 w-10 rounded-lg shrink-0" />
                      <div className="space-y-2 flex-1">
                        <Skeleton className="h-4 w-36" />
                        <Skeleton className="h-3 w-20" />
                      </div>
                    </div>
                    <Skeleton className="h-5 w-14 rounded-full shrink-0" />
                  </div>
                  <Skeleton className="h-3 w-full" />
                  <div className="flex gap-2 pt-1 pl-[52px]">
                    <Skeleton className="h-8 flex-1 rounded-md" />
                    <Skeleton className="h-8 w-8 rounded-md" />
                    <Skeleton className="h-8 w-8 rounded-md" />
                  </div>
                </div>
              ))}
            </div>
          ) : store.frameworks.length === 0 ? (
            <EmptyState
              icon={Layers}
              title="No custom frameworks yet"
              description="Create your first framework to start defining custom ESG disclosures."
              action={<NewFrameworkButton onClick={openCreate} />}
            />
          ) : (
            <div className="grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2">
              {store.frameworks.map((fw) => (
                <FrameworkCard
                  key={fw._id}
                  framework={fw}
                  onEdit={openEdit}
                  onDelete={setDeleteTarget}
                />
              ))}
            </div>
          )}
        </div>

        <FrameworkFormDialog
          key={formTarget?._id ?? "new"}
          open={formOpen}
          onClose={() => setFormOpen(false)}
          onSubmit={handleFrameworkSubmit}
          framework={formTarget}
        />

        {/* ── Delete confirm ────────────────────────────────────────────────────── */}
        <ConfirmDialog
          open={Boolean(deleteTarget)}
          onOpenChange={(v) => !v && setDeleteTarget(null)}
          title="Deactivate Framework?"
          description={`"${deleteTarget?.name}" will be deactivated and hidden from all framework pickers. Existing data entries will not be affected.`}
          confirmLabel="Deactivate"
          cancelLabel="Cancel"
          tone="danger"
          onConfirm={handleDelete}
        />
      </div>
    );
  },
);
